// src/components/game/TypingSpeed.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useScore } from '../../context/ScoreContext';

const TypingSpeed = ({ isDarkMode }) => {
  const { updateScore, getScore } = useScore();
  const [bestScore, setBestScore] = useState(0);

  const codeSnippets = [
    'function hello() { return "Hello World!"; }',
    'const sum = (a, b) => a + b;',
    'import React from "react";',
    'export default function App() { return <div />; }',
    'class Person { constructor(name) { this.name = name; } }',
    'const [count, setCount] = useState(0);',
    'useEffect(() => { console.log("mounted"); }, []);',
    'const handleClick = () => alert("Clicked!");',
    'const arr = [1, 2, 3, 4, 5].map(n => n * 2);',
    'fetch("/api/data").then(res => res.json()).then(data => console.log(data));',
    'const Greeting = ({ name }) => <h1>Hello {name}!</h1>;',
    'export const metadata = { title: "My App", description: "Awesome app" };'
  ];

  const [currentText, setCurrentText] = useState('');
  const [input, setInput] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [wpm, setWpm] = useState(0);
  const [accuracy, setAccuracy] = useState(100);
  const [isFinished, setIsFinished] = useState(false);
  const [errors, setErrors] = useState(0);
  const [rawWpm, setRawWpm] = useState(0);
  // ✅ charactersTyped - გამოიყენება სტატისტიკაში
  const [charactersTyped, setCharactersTyped] = useState(0);
  const [time, setTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const saved = getScore('typing');
    setBestScore(saved || 0);
  }, [getScore]);

  useEffect(() => {
    const initGame = () => {
      const random = codeSnippets[Math.floor(Math.random() * codeSnippets.length)];
      setCurrentText(random);
      setInput('');
      setStartTime(null);
      setWpm(0);
      setAccuracy(100);
      setIsFinished(false);
      setErrors(0);
      setRawWpm(0);
      setCharactersTyped(0);
      setTime(0);
      if (timerInterval) clearInterval(timerInterval);
      setTimerInterval(null);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };
    
    initGame();
    return () => {
      if (timerInterval) clearInterval(timerInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newText = () => {
    const random = codeSnippets[Math.floor(Math.random() * codeSnippets.length)];
    setCurrentText(random);
    setInput('');
    setStartTime(null);
    setWpm(0);
    setAccuracy(100);
    setIsFinished(false);
    setErrors(0);
    setRawWpm(0);
    setCharactersTyped(0);
    setTime(0);
    if (timerInterval) clearInterval(timerInterval);
    setTimerInterval(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInput = (e) => {
    const value = e.target.value;
    setInput(value);
    setCharactersTyped(value.length);

    if (!startTime) {
      setStartTime(Date.now());
      const interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
      setTimerInterval(interval);
    }

    let errorCount = 0;
    for (let i = 0; i < value.length; i++) {
      if (i < currentText.length) {
        if (value[i] !== currentText[i]) {
          errorCount++;
        }
      }
    }
    setErrors(errorCount);

    const elapsedMinutes = (Date.now() - startTime) / 60000;
    if (elapsedMinutes > 0) {
      const raw = Math.round((value.length / 5) / elapsedMinutes);
      setRawWpm(raw);
    }

    if (value.length === currentText.length) {
      const endTime = Date.now();
      const timeInMinutes = (endTime - startTime) / 60000;
      const words = currentText.split(' ').length;
      const wpmCalc = Math.round(words / timeInMinutes);
      setWpm(wpmCalc);
      const accCalc = Math.round(((currentText.length - errorCount) / currentText.length) * 100);
      setAccuracy(accCalc);
      setIsFinished(true);
      if (timerInterval) clearInterval(timerInterval);
      setTimerInterval(null);
      
      const baseScore = Math.round((wpmCalc * accCalc) / 100);
      const bonus = wpmCalc > 60 ? 5 : wpmCalc > 40 ? 3 : wpmCalc > 20 ? 1 : 0;
      const finalScore = Math.min(30, baseScore + bonus);
      if (finalScore > bestScore) {
        updateScore('typing', finalScore);
        setBestScore(getScore('typing'));
      } else {
        updateScore('typing', finalScore);
        setBestScore(getScore('typing'));
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getProgress = () => {
    if (currentText.length === 0) return 0;
    return Math.round((input.length / currentText.length) * 100);
  };

  const getColor = (char, idx) => {
    if (idx >= input.length) return isDarkMode ? '#444' : '#ccc';
    if (input[idx] === char) return '#4ade80';
    return '#f87171';
  };

  const pageStyle = {
    minHeight: '100vh',
    background: isDarkMode 
      ? 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)' 
      : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
    padding: 'clamp(12px, 3vw, 20px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const containerStyle = {
    maxWidth: 'min(750px, 95vw)',
    width: '100%',
    background: isDarkMode 
      ? 'rgba(26, 26, 46, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)',
    padding: 'clamp(15px, 4vw, 30px)',
    borderRadius: 'clamp(16px, 2vw, 20px)',
    backdropFilter: 'blur(20px)',
    boxShadow: isDarkMode 
      ? '0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)' 
      : '0 20px 60px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
    animation: 'slideUp 0.6s ease-out'
  };

  const textDisplayStyle = {
    fontFamily: 'monospace',
    fontSize: 'clamp(16px, 2.5vw, 24px)',
    lineHeight: 'clamp(1.6, 2vw, 1.8)',
    padding: 'clamp(12px, 2vw, 20px)',
    background: isDarkMode ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.02)',
    borderRadius: 'clamp(10px, 1.5vw, 12px)',
    marginBottom: 'clamp(15px, 2vw, 20px)',
    minHeight: 'clamp(60px, 10vw, 120px)',
    color: isDarkMode ? '#fff' : '#000',
    wordBreak: 'break-all',
    border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
    overflow: 'auto',
    maxHeight: 'clamp(150px, 20vw, 200px)'
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          @keyframes cursorBlink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          .game-title {
            background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-size: 200% auto;
            animation: shimmer 3s linear infinite;
          }
          .progress-bar {
            height: 4px;
            background: linear-gradient(90deg, #43e97b, #38f9d7);
            border-radius: 2px;
            transition: width 0.3s ease;
          }
          .cursor {
            display: inline-block;
            width: 2px;
            height: 24px;
            background: #4ade80;
            animation: cursorBlink 0.8s infinite;
            vertical-align: middle;
          }
        `}
      </style>

      <div style={containerStyle}>
        <Link to="/games" style={{
          display: 'inline-block',
          padding: 'clamp(6px, 1vw, 8px) clamp(16px, 2vw, 20px)',
          marginBottom: 'clamp(15px, 2vw, 20px)',
          background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
          color: isDarkMode ? '#fff' : '#000',
          textDecoration: 'none',
          borderRadius: 'clamp(10px, 1.5vw, 12px)',
          transition: 'all 0.3s',
          backdropFilter: 'blur(10px)',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
          fontSize: 'clamp(13px, 1.5vw, 15px)'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateX(-5px)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateX(0)';
          e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
        }}>
          ← უკან
        </Link>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 'clamp(12px, 1.5vw, 15px)',
          flexWrap: 'wrap',
          gap: 'clamp(8px, 1vw, 10px)'
        }}>
          <h1 style={{ margin: 0, fontSize: 'clamp(20px, 3vw, 32px)' }}>
            <span className="game-title">⌨️ Typing Speed</span>
          </h1>
          <div style={{ display: 'flex', gap: 'clamp(10px, 1.5vw, 15px)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: '#4ade80', fontWeight: 'bold', fontSize: 'clamp(13px, 1.5vw, 15px)' }}>
              🏆 {bestScore}
            </span>
            {!isFinished && input.length > 0 && (
              <span style={{ color: isDarkMode ? '#aaa' : '#666', fontSize: 'clamp(13px, 1.5vw, 15px)' }}>
                ⏱️ {formatTime(time)}
              </span>
            )}
          </div>
        </div>

        <p style={{ 
          color: isDarkMode ? '#aaa' : '#666',
          fontSize: 'clamp(12px, 1.5vw, 16px)',
          marginBottom: 'clamp(15px, 2vw, 20px)'
        }}>
          აკრიფე კოდი რაც შეიძლება სწრაფად
        </p>

        {/* Stats Bar - charactersTyped-ით */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
          gap: 'clamp(6px, 1vw, 8px)',
          marginBottom: 'clamp(15px, 2vw, 20px)',
          padding: 'clamp(10px, 1.5vw, 15px)',
          background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
          borderRadius: 'clamp(10px, 1.5vw, 12px)',
          textAlign: 'center'
        }}>
          <div>
            <div style={{ fontSize: 'clamp(16px, 2vw, 18px)' }}>📊</div>
            <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>
              {getProgress()}%
            </div>
            <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>პროგრესი</div>
          </div>
          <div>
            <div style={{ fontSize: 'clamp(16px, 2vw, 18px)', color: '#4ade80' }}>✅</div>
            <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>
              {input.length - errors}
            </div>
            <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>სწორი</div>
          </div>
          <div>
            <div style={{ fontSize: 'clamp(16px, 2vw, 18px)', color: '#f87171' }}>❌</div>
            <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>{errors}</div>
            <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>შეცდომა</div>
          </div>
          <div>
            <div style={{ fontSize: 'clamp(16px, 2vw, 18px)', color: '#38f9d7' }}>⚡</div>
            <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>
              {rawWpm || 0}
            </div>
            <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>WPM</div>
          </div>
          {/* ✅ charactersTyped - ახალი სტატისტიკა */}
          <div>
            <div style={{ fontSize: 'clamp(16px, 2vw, 18px)', color: '#a78bfa' }}>📝</div>
            <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>
              {charactersTyped}
            </div>
            <div style={{ fontSize: 'clamp(8px, 0.8vw, 9px)', color: isDarkMode ? '#666' : '#999' }}>სიმბოლო</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{
          width: '100%',
          height: 'clamp(3px, 0.5vw, 4px)',
          background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
          borderRadius: '2px',
          marginBottom: 'clamp(15px, 2vw, 20px)',
          overflow: 'hidden'
        }}>
          <div 
            className="progress-bar"
            style={{ width: `${getProgress()}%` }}
          />
        </div>

        {/* Text Display */}
        <div style={textDisplayStyle}>
          {currentText.split('').map((char, idx) => {
            const isActive = idx === input.length && !isFinished;
            
            return (
              <span key={idx} style={{ 
                color: getColor(char, idx),
                background: isActive ? (isDarkMode ? 'rgba(74,222,128,0.15)' : 'rgba(74,222,128,0.1)') : 'transparent',
                borderRadius: '2px',
                padding: isActive ? '0 2px' : '0',
                fontSize: 'clamp(14px, 2.5vw, 24px)'
              }}>
                {char}
                {isActive && <span className="cursor" style={{ height: 'clamp(18px, 3vw, 24px)' }} />}
              </span>
            );
          })}
        </div>

        <input
          ref={inputRef}
          value={input}
          onChange={handleInput}
          disabled={isFinished}
          placeholder="დაიწყეთ აკრეფა..."
          style={{
            width: '100%',
            padding: 'clamp(12px, 2vw, 18px)',
            fontSize: 'clamp(14px, 2vw, 20px)',
            fontFamily: 'monospace',
            borderRadius: 'clamp(10px, 1.5vw, 12px)',
            border: '2px solid',
            borderColor: isFinished 
              ? '#4ade80' 
              : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
            background: isDarkMode ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.02)',
            color: isDarkMode ? '#fff' : '#000',
            outline: 'none',
            transition: 'all 0.3s',
            opacity: isFinished ? 0.6 : 1,
            boxShadow: isFinished 
              ? '0 0 40px rgba(74,222,128,0.1)' 
              : 'none',
            WebkitAppearance: 'none',
            touchAction: 'manipulation'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#667eea';
            e.target.style.boxShadow = '0 0 40px rgba(102,126,234,0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
            e.target.style.boxShadow = 'none';
          }}
        />

        {isFinished && (
          <div style={{
            marginTop: 'clamp(15px, 2vw, 20px)',
            padding: 'clamp(16px, 3vw, 30px)',
            background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
            borderRadius: 'clamp(14px, 2vw, 16px)',
            color: '#000',
            animation: 'slideUp 0.3s ease-out',
            boxShadow: '0 4px 30px rgba(74,222,128,0.3)'
          }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
              gap: 'clamp(10px, 1.5vw, 15px)',
              textAlign: 'center'
            }}>
              <div>
                <div style={{ fontSize: 'clamp(10px, 1vw, 12px)', opacity: 0.7 }}>✅ დასრულდა!</div>
                <div style={{ fontSize: 'clamp(24px, 4vw, 28px)', fontWeight: 'bold' }}>🎉</div>
              </div>
              <div>
                <div style={{ fontSize: 'clamp(10px, 1vw, 12px)', opacity: 0.7 }}>სიჩქარე</div>
                <div style={{ fontSize: 'clamp(20px, 3vw, 24px)', fontWeight: 'bold' }}>{wpm} WPM</div>
              </div>
              <div>
                <div style={{ fontSize: 'clamp(10px, 1vw, 12px)', opacity: 0.7 }}>სიზუსტე</div>
                <div style={{ fontSize: 'clamp(20px, 3vw, 24px)', fontWeight: 'bold' }}>{accuracy}%</div>
              </div>
              <div>
                <div style={{ fontSize: 'clamp(10px, 1vw, 12px)', opacity: 0.7 }}>ქულა</div>
                <div style={{ fontSize: 'clamp(20px, 3vw, 24px)', fontWeight: 'bold' }}>
                  {Math.round((wpm * accuracy) / 100)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 'clamp(10px, 1vw, 12px)', opacity: 0.7 }}>📝</div>
                <div style={{ fontSize: 'clamp(20px, 3vw, 24px)', fontWeight: 'bold' }}>
                  {charactersTyped}
                </div>
                <div style={{ fontSize: 'clamp(10px, 1vw, 12px)', opacity: 0.7 }}>სიმბოლო</div>
              </div>
            </div>
            <button onClick={newText} style={{
              padding: 'clamp(10px, 1.5vw, 12px) clamp(20px, 3vw, 30px)',
              background: 'rgba(0,0,0,0.15)',
              color: '#000',
              border: 'none',
              borderRadius: 'clamp(10px, 1.5vw, 12px)',
              cursor: 'pointer',
              marginTop: 'clamp(12px, 1.5vw, 15px)',
              fontSize: 'clamp(14px, 1.5vw, 16px)',
              fontWeight: 'bold',
              transition: 'all 0.3s',
              width: '100%',
              backdropFilter: 'blur(10px)',
              touchAction: 'manipulation'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(0,0,0,0.25)';
              e.target.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(0,0,0,0.15)';
              e.target.style.transform = 'scale(1)';
            }}>
              🔄 ახალი ტექსტი
            </button>
          </div>
        )}

        <div style={{
          marginTop: 'clamp(12px, 1.5vw, 15px)',
          padding: 'clamp(10px, 1vw, 12px)',
          textAlign: 'center',
          fontSize: 'clamp(10px, 1vw, 11px)',
          color: isDarkMode ? '#555' : '#ccc',
          borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)'
        }}>
          💡 Esc - გადატვირთვა • Tab - ინდენტაცია
        </div>
      </div>
    </div>
  );
};

export default TypingSpeed;